import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box, Breadcrumbs } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid2";
import * as Yup from "yup";
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "../../../../components/TextField";
import GridTable from "../../../../components/GridTable";
import ActionMenu from "../../../../components/ActionMenu";
import Dialog from "../../../../components/Dialog";
import Loader from "../../../../components/Loader";
import Button from "../../../../components/Button";
import { successMessage, errorMessage } from "../../../../components/Toaster";
import { getSectionLinesBySectionID, createSectionLine, updateSectionLine, deleteSectionLine } from "./api";
import { ApiResponse, SectionLineData, CreateSectionLineParams, UpdateSectionLineParams } from "./interface";
import { getErrorMessage } from "../../../../utils/error";

const SectionLines: React.FC = () => {
    const location = useLocation();
    const company_name = location.state?.company_name;
    const section_name = location.state?.section_name;
    const { section_id, company_id } = useParams<{ section_id: string; company_id: string }>();
    const [sectionLines, setSectionLines] = useState<SectionLineData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editSectionLine, setEditSectionLine] = useState<SectionLineData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        sectionLineName: Yup.string().required("Section line name is required"),
    });

    const columns: GridColDef[] = [
        { field: "section_line_name", headerName: "Section Line Name", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        if (section_id) {
            const fetchSectionLines = async () => {
                setLoading((prev) => ({ ...prev, fetch: true }));
                try {
                    const sectionLinesData: ApiResponse<SectionLineData[]> = await getSectionLinesBySectionID(section_id);
                    if (sectionLinesData.status && sectionLinesData.data) {
                        setSectionLines(sectionLinesData.data);
                    } else {
                        errorMessage(sectionLinesData.message || "Failed to fetch section lines.");
                    }
                } catch (error) {
                    console.error("Error fetching section lines:", error);
                    const message = getErrorMessage(error);
                    errorMessage(message || "Error fetching section lines");
                } finally {
                    setLoading((prev) => ({ ...prev, fetch: false }));
                }
            };

            fetchSectionLines();
        }
    }, [section_id]);

    const openDialog = () => setDialogOpen(true);

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditSectionLine(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditSectionLine(params.row as SectionLineData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const confirmDialog = async (values: any, { resetForm }: { resetForm: () => void }) => {
        if (editSectionLine) {
            await handleUpdate(values);
        } else {
            await handleCreate(values);
        }
        closeDialog(resetForm);
    };

    const handleCreate = async (values: { sectionLineName: string }) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const newSectionLine: CreateSectionLineParams = {
                section_id: section_id || "",
                section_line_name: values.sectionLineName,
            };

            const response = await createSectionLine(newSectionLine);

            if (response?.status) {
                setSectionLines((prev) => {
                    const currentSectionLines = Array.isArray(prev) ? prev : [];
                    return [
                        ...currentSectionLines,
                        {
                            section_line_id: response?.data?.section_line_id,
                            section_id: section_id || "",
                            section_line_name: values.sectionLineName,
                        },
                    ];
                });
                successMessage(response.message || "Section line created successfully!");
            } else {
                errorMessage(response?.message || "Failed to create section line.");
            }
        } catch (error) {
            console.error("Error creating section line:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: { sectionLineName: string }) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedSectionLine: UpdateSectionLineParams = {
                section_line_id: editSectionLine!.section_line_id || "",
                section_line_name: values.sectionLineName,
            };

            const response = await updateSectionLine(updatedSectionLine);

            if (response?.status) {
                setSectionLines((prev) =>
                    prev.map((line) =>
                        line.section_line_id === updatedSectionLine.section_line_id
                            ? { ...line, section_line_name: values.sectionLineName }
                            : line
                    )
                );
                successMessage(response.message || "Section line updated successfully!");
            } else {
                errorMessage(response?.message || "Failed to update section line.");
            }
        } catch (error) {
            console.error("Error updating section line:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));

        if (itemToDelete) {
            try {
                const response = await deleteSectionLine(itemToDelete.row.section_line_id);

                if (response?.status) {
                    setSectionLines((prev) =>
                        prev.filter((line) => line.section_line_id !== itemToDelete.row.section_line_id)
                    );
                    successMessage(response.message || "Section line deleted successfully!");
                } else {
                    errorMessage(response?.message || "Failed to delete section line.");
                }
            } catch (error) {
                console.error("Error deleting section line:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "An unexpected error occurred. Please try again.");
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };

    return (
        <Box>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: "100%", height: "50%" }}>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "40px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/company">
                                    Company
                                </Link>
                                <Link color="primary" to="/company">{company_name}</Link>
                                <Link
                                    color="primary"
                                    to={`/company/${company_id}/sections`}
                                    state={{ company_name: company_name }}
                                >
                                    {section_name}
                                </Link>
                                <Link to={`/company/${company_id}/sections/${section_id}/section_lines`} state={{ company_name: company_name, section_name: section_name }} style={{ color: "primary" }}>
                                    Section Lines
                                </Link>
                            </Breadcrumbs>
                        </div>
                        <div>
                            <div className="d-flex justify-end">
                                <div className="d-flex justify-between w-100">
                                    <TextField
                                        className="w-25"
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <Button
                                        label="Add"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDialog}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            style={{ height: '100%', width: '100%' }}
                            className="ag-theme-alpine m-t-20"
                        >
                            <GridTable
                                rows={sectionLines}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.section_line_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                sectionLineName: editSectionLine
                                    ? editSectionLine.section_line_name
                                    : "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                            }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={
                                        !loading.update && !loading.create
                                            ? editSectionLine
                                                ? "Edit Section Line"
                                                : "Add New Section Line"
                                            : ""
                                    }
                                    confirmButtonText={
                                        !loading.update && !loading.create
                                            ? editSectionLine
                                                ? "Update"
                                                : "Add"
                                            : ""
                                    }
                                    cancelButtonText={
                                        !loading.update && !loading.create ? "Cancel" : ""
                                    }
                                    icon={<FormatLineSpacingIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.update || loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={12}>
                                                            <TextField
                                                                id="sectionLineName"
                                                                name="sectionLineName"
                                                                label="Section Line Name"
                                                                required
                                                                value={values.sectionLineName}
                                                                onChange={handleChange}
                                                                error={
                                                                    touched.sectionLineName &&
                                                                    Boolean(errors.sectionLineName)
                                                                }
                                                                helperText={
                                                                    touched.sectionLineName &&
                                                                    errors.sectionLineName
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title="Delete Confirmation"
                            confirmButtonText="Delete"
                            cancelButtonText="Cancel"
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader color="primary" size={40} />
                                    ) : (
                                        "Are you sure you want to delete this section line?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </Box>
    );
};

export default SectionLines;
